import { request } from './client';

/**
 * Actions: the jobs a visit leaves behind.
 *
 * A free action closes on the owner's word. An approved one travels a chain of approvers,
 * and the person who answered it is never on that chain.
 */

const base = '/api/v1/actions';
const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export type ActionKind = 'Free' | 'Approved';
export type ActionAssignee = 'Position' | 'User' | 'Department' | 'Company';
export type ActionStatus = 'Open' | 'InApproval' | 'SentBack' | 'Completed' | 'Unresolved';
export type StepDecision = 'Waiting' | 'Approved' | 'SentBack';

export interface Assignable {
  kind: ActionAssignee;
  id: string;
  name: string;
  detail: string | null;

  /**
   * Whether this candidate could actually answer the action.
   *
   * <p>False for somebody with no permission to answer one, or whose reach does not cover
   * the store — a seat for approving actions rather than doing them, most often. Null for
   * the "anybody" list, where it is not worked out per person; a choice from there is
   * refused when the action is raised instead.</p>
   */
  canAnswer: boolean | null;
}

/** The three lists the raise panel offers, in the order it offers them. */
export interface AssignableGroup {
  /** Seats. An action given to one stays put when the person in it changes. */
  seats: Assignable[];

  /** The people sitting in those seats today. */
  people: Assignable[];
}

/** Where an action can go. The kind of action decides which of these are offered. */
export type ActionTarget = 'store' | 'headOfficeDepartments' | 'headOfficePeople';

export interface AssignableSet {
  /** The audited store's own seats and the people in them. */
  store: AssignableGroup;

  /** Seats in the departments marked as head office, and the people in them. */
  headOfficeDepartments: AssignableGroup;

  /**
   * People named head office one by one, whatever department they sit in.
   *
   * Separate from the list above because a department that serves head office and the
   * stores at once should not have to be decided one way for everybody in it. People
   * only — this route names the person, not the chair.
   */
  headOfficePeople: AssignableGroup;

  /**
   * Whether this visit's checklist defines an approval chain.
   *
   * Without one there is no approved action to raise: it would be answered and then sit
   * for ever with nobody able to close it.
   */
  hasApprovalChain: boolean;

  /**
   * Whether any department is marked as head office at all.
   *
   * An empty head-office list means one of two different things, and they are fixed on
   * different screens: nobody is seated there, or nobody has said which departments are
   * head office.
   */
  hasHeadOfficeDepartments: boolean;

  /** The same question for the other route: has anybody been named one. */
  hasHeadOfficePeople: boolean;
}

export interface ActionPlan {
  /** ISO date, no time. */
  plannedFor: string;
  reason: string;
  enteredBy: string;
  enteredAt: string;
}

export interface ActionStep {
  order: number;
  approver: string;
  decision: StepDecision;
  note: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  isCurrent: boolean;
}

/**
 * A file attached to an action to show it was done.
 *
 * The url is minted by the server on each read and expires; it is not somewhere to keep.
 */
export interface ActionEvidence {
  id: string;
  fileName: string | null;
  contentType: string | null;
  sizeInBytes: number | null;
  uploadedBy: string;
  uploadedAt: string;
  url: string | null;
}

export interface Action {
  id: string;
  reference: string | null;
  kind: ActionKind;
  status: ActionStatus;

  auditId: string;
  auditReference: string | null;
  questionId: string;
  questionText: string;

  locationId: string;
  locationCode: string;
  locationName: string;

  raisedBy: string;
  raisedAt: string;

  assignee: ActionAssignee;
  assigneeId: string;
  assigneeName: string;

  problem: string;
  /** The auditor's deadline. It does not move. */
  dueOn: string;
  isOverdue: boolean;
  /** Past its date: a new promise is owed before it can be answered. */
  needsNewPlan: boolean;

  response: string | null;
  respondedBy: string | null;
  respondedAt: string | null;
  completedAt: string | null;

  /** Every promise the owner has made, oldest first. Added to, never rewritten. */
  plans: ActionPlan[];
  steps: ActionStep[];
  /** What was attached to show the work. Oldest first. */
  evidence: ActionEvidence[];

  isMine: boolean;
  awaitsMyApproval: boolean;
}

export interface RaiseAction {
  questionId: string;
  kind: ActionKind;
  assignee: ActionAssignee;
  assigneeId: string;
  problem: string;
  dueOn: string;
}

export const actions = {
  list: (params: { mine?: boolean; status?: ActionStatus; overdue?: boolean } = {}) => {
    const query = new URLSearchParams();
    if (params.mine) query.set('mine', 'true');
    if (params.status) query.set('status', params.status);
    if (params.overdue) query.set('overdue', 'true');

    const q = query.toString();
    return request<Action[]>(q ? `${base}?${q}` : base);
  },

  get: (id: string) => request<Action>(`${base}/${id}`),

  /** Who this audit's findings can be handed to. */
  assignable: (auditId: string) =>
    request<AssignableSet>(`/api/v1/audits/${auditId}/assignable`),

  raise: (auditId: string, body: RaiseAction) =>
    request<Action>(`/api/v1/audits/${auditId}/actions`, json('POST', body)),

  /** Hands it to somebody else. Only before anybody has answered it. */
  reassign: (id: string, assignee: ActionAssignee, assigneeId: string) =>
    request<Action>(`${base}/${id}/assignee`, json('PUT', { assignee, assigneeId })),

  /** When it will be done, and why. Added beside the promises before it. */
  plan: (id: string, plannedFor: string, reason: string) =>
    request<Action>(`${base}/${id}/plan`, json('POST', { plannedFor, reason })),

  respond: (id: string, response: string) =>
    request<Action>(`${base}/${id}/respond`, json('POST', { response })),

  unresolved: (id: string, response: string) =>
    request<Action>(`${base}/${id}/unresolved`, json('POST', { response })),

  approve: (id: string, note?: string) =>
    request<Action>(`${base}/${id}/approve`, json('POST', { note })),

  sendBack: (id: string, note: string) =>
    request<Action>(`${base}/${id}/send-back`, json('POST', { note })),

  /**
   * Multipart, so no Content-Type header: the browser sets it with the boundary it chose,
   * and naming it ourselves produces a body the server cannot parse.
   */
  attach: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<Action>(`${base}/${id}/evidence`, { method: 'POST', body: form });
  },

  detach: (id: string, evidenceId: string) =>
    request<Action>(`${base}/${id}/evidence/${evidenceId}`, { method: 'DELETE' }),
};
